import React, { useEffect, useState, useMemo } from 'react';
import { Row, Card, Button, Form, FormGroup } from 'react-bootstrap';
import {
    getListAddOns,
    getListVehicleSegments,
    getVehicleAssignmentDetails,
    useVehicleAssignmentsState,
    useAccountState,
    getTripSettings,
} from '../../../../Context';

import Loading from '../../../../Components/Loading';
import { InfoField } from '../../../../Components/Reusable';
import SimpleTable from '../../../../Components/SimpleTable';
import CustomAccordion from '../../../../Components/CustomAccordion';
import MockupListingLayout from '../../../../Components/MockupLayout';
import { calculateTotal, formatCurrency, convertQueryStringToObject } from '../../../../utils/helper';
import { getListDispatch, getListDispatchDocuments } from '../../../../Context/actions/vehicleAssignmentsActions';

const filenameRender = (item) => {
    return item.fileUrl ? (
        <a href={item.fileUrl} rel="noreferrer noopener" target="_blank">
            {item.fileName}
        </a>
    ) : (
        <p>{item.fileName}</p>
    );
};

const descriptionRender = (item, key) => <p className="truncate">{item[key]}</p>;

const currencyViewRenderer = (item, key) => (isNaN(item[key]) ? '' : `$${formatCurrency(item[key])}`);

const BookingVehicleAssignments = (props) => {
    const [collapseItem, handleCollapse] = useState({ 1: false });
    const vehicleAssignmentId = props.match.params.id;
    const queryObj = convertQueryStringToObject(props.location.search);

    const {
        vehicleAssignments: { vehicleAssignmentDetail, segments, addOns, documents, vehicles, loading },
        vehicleAssignmentsDispatch,
    } = useVehicleAssignmentsState();

    const {
        AccountsDispatch,
        tripSettings: { tripSettings },
    } = useAccountState();

    useEffect(() => {
        (async () => {
            try {
                await getTripSettings(AccountsDispatch);
                await getVehicleAssignmentDetails(vehicleAssignmentsDispatch, queryObj.bookingId, vehicleAssignmentId);

                await Promise.all([
                    getListVehicleSegments(vehicleAssignmentsDispatch, vehicleAssignmentId),
                    getListAddOns(vehicleAssignmentsDispatch, queryObj.bookingId, vehicleAssignmentId),
                    getListDispatch(vehicleAssignmentsDispatch, null, vehicleAssignmentId),
                    getListDispatchDocuments(vehicleAssignmentsDispatch, vehicleAssignmentId),
                ]);
            } catch (error) {
                console.error('Error loading assignment details', error);
            }
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleItemCollapse = (e) => {
        const { id } = e.target;
        handleCollapse((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const totalRenderer = (data, key) => `$${formatCurrency(calculateTotal(data, key))}`;

    const segmentColumns = useMemo(
        () => [
            ['startAddress', 'Start Address'],
            ['departureTime', 'Departure Time'],
            ['destinationAddress', 'Destination Address'],
            ['arrivalTime', 'Arrival Time'],
            tripSettings.useMetric
                ? ['kilometers', 'Km', null, calculateTotal(segments, 'kilometers')]
                : ['miles', 'Miles', null, calculateTotal(segments, 'miles')],
        ],
        [tripSettings.useMetric, segments]
    );

    const vehicleColumns = useMemo(() => {
        if (vehicleAssignmentDetail.documentTypeID === 2) {
            return [['vehicleDisplayName', 'Vehicle Type']];
        }
        return [
            ['vehicleDisplayName', 'Vehicle Type'],
            [
                'vehiclePrice',
                'Vehicle Price',
                (item) => currencyViewRenderer(item, 'vehiclePrice'),
                totalRenderer(vehicles, 'vehiclePrice'),
            ],
            ['salesTaxDesc', 'Tax Description'],
            ['salesTax', 'Tax', (item) => currencyViewRenderer(item, 'salesTax'), totalRenderer(vehicles, 'salesTax')],
        ];
    }, [vehicleAssignmentDetail.documentTypeID, vehicles]);

    const addonColumns = useMemo(
        () => [
            ['description', 'Description'],
            ['quantity', 'Quantity'],
            ['price', 'Addon Price', (item) => currencyViewRenderer(item, 'price'), totalRenderer(addOns, 'price')],
            ['salesTaxDescription', 'Tax Description'],
            ['salesTax', 'Tax', (item) => currencyViewRenderer(item, 'salesTax'), totalRenderer(addOns, 'salesTax')],
        ],
        [addOns]
    );

    const documentColumns = useMemo(
        () => [
            ['fileName', 'File Name', filenameRender],
            ['description', 'Description', (item) => descriptionRender(item, 'description')],
        ],
        []
    );

    const getBackTarget = () => {
        if (queryObj.backUrl) return queryObj.backUrl;
        const bookingDetailsUrl = `/bookings/${queryObj.bookingId}`;
        if (queryObj.originBackTo && queryObj.originBackUrl) {
            return `${bookingDetailsUrl}?backTo=${encodeURIComponent(queryObj.originBackTo)}&backUrl=${encodeURIComponent(
                queryObj.originBackUrl
            )}`;
        }
        return bookingDetailsUrl;
    };

    // --- Main Render ---
    return (
        <MockupListingLayout
            title={`Route Information ${vehicleAssignmentDetail.cancelledAt ? '(Cancelled)' : ''}`}
            titleIconClass={'fas fa-calendar-check'}
            textColor={tripSettings?.textColor}
            buttons={
                <Button
                    onClick={() => props.history.push(getBackTarget())}
                    style={{ backgroundColor: `${tripSettings?.buttonColor}`, borderColor: 'transparent' }}
                >
                    {queryObj.backTo ? `BACK TO ${queryObj.backTo.toUpperCase()}` : 'BACK TO BOOKING DETAILS'}
                </Button>
            }
        >
            {/* --- Trip Details & Segments --- */}
            <CustomAccordion
                id={1}
                title="Trip Details"
                collapseItem={collapseItem}
                handleItemCollapse={handleItemCollapse}
                sectionColor={tripSettings?.sectionColor}
                className="px-2"
            >
                {/* Route Description */}
                <Row className="mx-1">
                    <InfoField
                        label="Route Description"
                        value={vehicleAssignmentDetail.routeDescription}
                        colClass="col-12 col-md-6"
                    />
                </Row>

                <Card>
                    <Card.Body className="p-3">
                        <SimpleTable
                            fieldNames={segmentColumns}
                            mainColor={tripSettings?.mainColor}
                            data={segments.filter((data) => data.isLiveSegment !== 0 && data.liveHoursMiles !== 0)}
                            showFooter
                        />
                    </Card.Body>
                    <Card.Footer>
                        {/* Flight / Passenger Info */}
                        <Row className="mx-1">
                            <InfoField
                                label="# Of Passengers"
                                value={vehicleAssignmentDetail.numPassengers}
                                colClass="col-12 col-md-6 col-lg-3"
                            />
                            <InfoField
                                label="Airline"
                                value={vehicleAssignmentDetail.airline}
                                colClass="col-12 col-md-6 col-lg-3"
                            />
                            <InfoField
                                label="Airline Terminal"
                                value={vehicleAssignmentDetail.airlineTerminal}
                                colClass="col-12 col-md-6 col-lg-3"
                            />
                            <InfoField
                                label="Flight Number"
                                value={vehicleAssignmentDetail.flightNum}
                                colClass="col-12 col-md-6 col-lg-3"
                            />
                        </Row>

                        {/* Instructions */}
                        <Row className="mx-1">
                            {vehicleAssignmentDetail.pickupInstructions && (
                                <InfoField colClass="col-12 col-md-6 col-lg-6">
                                    <FormGroup>
                                        <Form.Label>Pickup Instructions</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            value={vehicleAssignmentDetail.pickupInstructions}
                                            disabled
                                        />
                                    </FormGroup>
                                </InfoField>
                            )}
                            {vehicleAssignmentDetail.destinationInstructions && (
                                <InfoField colClass="col-12 col-md-6 col-lg-6">
                                    <FormGroup>
                                        <Form.Label>Destination Instructions</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            value={vehicleAssignmentDetail.destinationInstructions}
                                            disabled
                                        />
                                    </FormGroup>
                                </InfoField>
                            )}
                        </Row>

                        {/* Additional Details */}
                        <Row className="mx-1">
                            {vehicleAssignmentDetail.additionalRouteDetails && (
                                <InfoField colClass="col-12 col-md-6 col-lg-6">
                                    <FormGroup>
                                        <Form.Label>Additional Route Details</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            value={vehicleAssignmentDetail.additionalRouteDetails}
                                            disabled
                                        />
                                    </FormGroup>
                                </InfoField>
                            )}
                            {vehicleAssignmentDetail.additionalTripReq && (
                                <InfoField colClass="col-12 col-md-6 col-lg-6">
                                    <FormGroup>
                                        <Form.Label>Additional Trip Requirements</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            value={vehicleAssignmentDetail.additionalTripReq}
                                            disabled
                                        />
                                    </FormGroup>
                                </InfoField>
                            )}
                        </Row>
                    </Card.Footer>
                </Card>
            </CustomAccordion>

            {/* --- Vehicles Table --- */}
            <CustomAccordion
                id={2}
                title="Vehicles"
                collapseItem={collapseItem}
                handleItemCollapse={handleItemCollapse}
                sectionColor={tripSettings?.sectionColor}
                className="px-2"
            >
                <SimpleTable
                    fieldNames={vehicleColumns}
                    mainColor={tripSettings?.mainColor}
                    data={vehicles}
                    showFooter={vehicleAssignmentDetail.documentTypeID !== 2}
                />
            </CustomAccordion>

            {/* --- Addons Table --- */}
            {vehicleAssignmentDetail.documentTypeID !== 2 && (
                <CustomAccordion
                    id={3}
                    title="Addons"
                    collapseItem={collapseItem}
                    handleItemCollapse={handleItemCollapse}
                    sectionColor={tripSettings?.sectionColor}
                    className="px-2"
                >
                    <SimpleTable
                        fieldNames={addonColumns}
                        data={addOns}
                        mainColor={tripSettings?.mainColor}
                        showFooter
                    />
                </CustomAccordion>
            )}

            {/* --- Route Documents --- */}
            <CustomAccordion
                id={4}
                title="Route Document"
                collapseItem={collapseItem}
                handleItemCollapse={handleItemCollapse}
                sectionColor={tripSettings?.sectionColor}
                className="px-2"
            >
                <SimpleTable fieldNames={documentColumns} data={documents} mainColor={tripSettings?.mainColor} />
            </CustomAccordion>

            {/* --- Loaders --- */}
            {loading && <Loading loading={loading} />}
        </MockupListingLayout>
    );
};

export default BookingVehicleAssignments;
